import { Base64 } from 'js-base64';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FiSearch, FiX, FiCalendar, FiChevronDown, FiPlayCircle, FiImage, FiFilm, FiGift, FiUsers } from 'react-icons/fi';
import logo from '../../../../assets/images/logo/Haylo-for-white-background.png';
import whiteLogo from '../../../../assets/images/logo/Haylo-logo-white.png';
import { baseURLPublic, URL_S3 } from '../../../../config/BaseUrl';
import { foregroundFor, HAYLO_PRIMARY } from '../../../SocialGalleryV2/PatronPage';
import { sortGalleryItems } from './GalleryToolbar';
import { MEDIA_FILTERS, mediaLabel, filterGalleryItems, thumbnailUrl } from './galleryPresentation';
import '../style/microsite-one.css';
import '../style/style.css';
import './event-gallery.css';

const LEGACY_CLASSES = { one: 'microsite-template', two: 'customer-microsite-two', three: 'Customer-microsite-default' };
const FOOTER_ACTIONS = { offer: ['Offers', 'Next visit offer', FiGift], function: ['Functions', 'Book your event', FiCalendar], membership: ['Members', 'Join the club', FiUsers] };
const validColor = (value, fallback) => /^#[0-9a-f]{6}$/i.test(value || '') ? value : fallback;

export default function EventGallery() {
  const { id } = useParams();
  const history = useHistory();
  let eventId;
  try { eventId = Base64.atob(id); } catch (_) { eventId = ''; }
  const [state, setState] = useState({ loading: true });
  const [retry, setRetry] = useState(0);
  const [sortOrder, setSortOrder] = useState('latest');
  const [mediaFilter, setMediaFilter] = useState('all');
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({ search: '', captured_from: '', captured_to: '' });
  const [filterError, setFilterError] = useState('');
  const [context, setContext] = useState(null);
  const tagEvent = useRef(null);

  const clearFilters = () => {
    setSearch(''); setDateFrom(''); setDateTo(''); setFilterError('');
    setAppliedFilters({ search: '', captured_from: '', captured_to: '' });
  };
  const applyFilters = (event) => {
    event.preventDefault();
    if (dateFrom && dateTo && dateFrom > dateTo) { setFilterError('From date/time cannot be after To date/time.'); return; }
    setFilterError('');
    setAppliedFilters({ search: search.trim(), captured_from: dateFrom, captured_to: dateTo });
  };

  useEffect(() => {
    const clearPassword = () => { try { localStorage.removeItem('galleryPasswordCheck'); } catch (_) { /* Storage may be disabled. */ } };
    window.addEventListener('beforeunload', clearPassword);
    return () => window.removeEventListener('beforeunload', clearPassword);
  }, []);

  useEffect(() => {
    let active = true;
    setState((previous) => previous.data && previous.eventId === eventId ? { ...previous, filtering: true } : { loading: true });
    setFilterError(''); setContext(null);
    if (!/^\d+$/.test(eventId)) { setState({ error: 'This gallery link is unavailable.' }); return undefined; }
    const hasFilters = Object.values(appliedFilters).some(Boolean);
    const request = hasFilters ? baseURLPublic.post(`social-gallery-item/${eventId}/search`, appliedFilters) : baseURLPublic.get(`social-gallery-item/${eventId}`);
    request.then(({ data }) => {
      if (!active) return;
      if (data.gallery?.gallery_password && !localStorage.getItem('galleryPasswordCheck')) {
        history.replace(`/social/gallery/password/${id}`); return;
      }
      if (!Array.isArray(data.gallery_items)) throw new Error('Invalid gallery');
      setState({ data, eventId });
      if (data.gallery?.tag_script && tagEvent.current !== eventId) {
        tagEvent.current = eventId;
        document.head.appendChild(document.createRange().createContextualFragment(data.gallery.tag_script));
      }
      // Only a deliberately followed recipient Gallery link supplies this tab's context.
      let token;
      try { token = sessionStorage.getItem(`sg-gallery-context:${eventId}`); } catch (_) { /* Public gallery still works. */ }
      if (/^[A-Za-z0-9_-]{43}$/.test(token || '')) {
        baseURLPublic.get(`social-gallery-v2/links/${encodeURIComponent(token)}`).then(({ data: recipient }) => {
          if (active && String(recipient.data.event_id) === eventId && recipient.data.gallery_visible) {
            setContext({ token, actions: recipient.data.actions || [] });
          }
        }).catch(() => { /* Revoked or unavailable contexts do not expose footer actions. */ });
      }
    }).catch((failure) => {
      if (!active) return;
      const errors = failure.response?.data?.errors;
      setFilterError(errors ? Object.values(errors).flat().join(' ') : 'Could not update the gallery. Please try again.');
      setState((previous) => previous.data && previous.eventId === eventId ? { ...previous, filtering: false, filterFailed: true } : { error: 'We could not load this gallery. Please try again.' });
    });
    return () => { active = false; };
  }, [eventId, id, retry, history, appliedFilters]);

  const data = state.data;
  const items = useMemo(() => filterGalleryItems(sortGalleryItems(data?.gallery_items || [], sortOrder), mediaFilter, ''), [data, sortOrder, mediaFilter]);
  const visibleCount = items.length;
  const template = LEGACY_CLASSES[data?.template_name] ? data.template_name : 'one';
  const branding = data?.branding || {};
  const background = validColor(branding.background_color, '#FFFFFF');
  const endColor = validColor(branding.background_gradient_color, HAYLO_PRIMARY);
  const accent = validColor(branding.accent_color, HAYLO_PRIMARY);
  const angle = Number.isInteger(Number(branding.background_gradient_angle)) ? Math.min(360, Math.max(0, Number(branding.background_gradient_angle))) : 135;
  const custom = branding.custom_background === true;
  const foreground = custom ? foregroundFor(background) : template === 'two' ? '#FFFFFF' : '#20242b';
  const defaultLogo = foreground === '#FFFFFF' ? whiteLogo : logo;
  const style = { color: foreground, '--eg-accent': accent, '--eg-accent-text': foregroundFor(accent),
    ...(custom ? { backgroundColor: background, backgroundImage: branding.background_type === 'gradient' ? `linear-gradient(${angle}deg, ${background}, ${endColor})` : 'none' } : {}) };
  const mediaBase = (data?.media_base_url || URL_S3).replace(/\/$/, '');
  const actions = (context?.actions || []).filter((action) => FOOTER_ACTIONS[action.id]);

  return <>
    <Helmet><meta name="referrer" content="no-referrer" /></Helmet>
    <main className={`sg-event-gallery ${LEGACY_CLASSES[template]} ${custom ? 'eg-custom-background' : ''}`} style={style}>
      <div className="eg-shell">
        <header className="eg-header">
          <img className="eg-venue-logo" src={branding.logo_url || defaultLogo} alt={branding.logo_url ? 'Venue logo' : 'The Haylo'} />
          <button type="button" className="eg-search-toggle" aria-label={searchOpen ? 'Close gallery search' : 'Search gallery'} aria-expanded={searchOpen} aria-controls="eg-search" onClick={() => { if (searchOpen) clearFilters(); setSearchOpen(!searchOpen); }}>{searchOpen ? <FiX /> : <FiSearch />}</button>
        </header>
        {state.loading ? <p className="eg-state" role="status">Loading your moments…</p> : state.error ? <div className="eg-state"><p role="alert">{state.error}</p><button type="button" onClick={() => setRetry(retry + 1)}>Try again</button></div> : <>
          <div className="eg-event-heading">
            <h1 className="eg-event-title"><FiCalendar aria-hidden="true" /><span>{data.event_name || 'Event Gallery'}</span></h1>
            <label className="eg-sort"><span className="eg-sr-only">Sort gallery</span><select value={sortOrder} onChange={(event) => setSortOrder(event.target.value)}><option value="latest">Latest first</option><option value="oldest">Oldest first</option></select><FiChevronDown aria-hidden="true" /></label>
          </div>
          {searchOpen && <form className="eg-search-panel" id="eg-search" onSubmit={applyFilters}>
            <label className="eg-person-search">Name, phone or email<input type="search" autoComplete="off" maxLength={255} autoFocus value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Guest name, full phone or email" /></label>
            <label>From date/time<input type="datetime-local" step={60} value={dateFrom} max={dateTo || undefined} onChange={(event) => setDateFrom(event.target.value)} /></label>
            <label>To date/time<input type="datetime-local" step={60} value={dateTo} min={dateFrom || undefined} onChange={(event) => setDateTo(event.target.value)} /></label>
            <div className="eg-search-actions"><button type="submit" disabled={state.filtering}>Apply</button><button type="button" onClick={clearFilters}>Clear</button></div>
            <small>Dates and times use {data.time_zone || 'the event timezone'}.</small>
          </form>}
          {filterError && <p className="eg-filter-error" role="alert">{filterError}{state.filterFailed && ' Showing previous results.'}</p>}
          {state.filtering && <p role="status">Updating results…</p>}
          <div className="eg-type-filters" role="group" aria-label="Media type">
            {MEDIA_FILTERS.map(([value, label]) => <button type="button" key={value} aria-pressed={mediaFilter === value} onClick={() => setMediaFilter(value)}>{label}</button>)}
          </div>
          <p className="eg-sr-only" role="status">{visibleCount} media items shown</p>
          {!items.length && <p className="eg-state">{data.gallery_items.length || Object.values(appliedFilters).some(Boolean) ? 'No moments match these filters.' : 'Photos and videos will appear here when available.'}</p>}
          <div className="eg-grid" aria-busy={!!state.filtering}>{items.map((item) => {
              const thumbnail = thumbnailUrl(item, mediaBase);
              const isVideo = ['video', 'boomerang'].includes(item.media_type);
              const Icon = isVideo ? FiFilm : FiImage;
              return <Link key={item.id} className="eg-media-tile" to={`/social/gallery/view/${Base64.btoa(String(item.id))}`} aria-label={`Open ${mediaLabel(item.media_type)} ${item.id}`}>
                {thumbnail ? <img src={thumbnail} alt="" loading="lazy" decoding="async" onError={(event) => { event.currentTarget.style.visibility = 'hidden'; }} /> : <Icon className="eg-placeholder-icon" aria-hidden="true" />}
                {isVideo && <FiPlayCircle className="eg-play" aria-hidden="true" />}
                <span className="eg-tile-meta"><span>{['gif', 'burst_gif'].includes(item.media_type) ? 'GIF' : <Icon aria-hidden="true" />}</span></span>
              </Link>;
          })}</div>
          {actions.length > 0 && <nav className="eg-venue-actions" aria-label="Actions for your shared photo">{actions.map(({ id: actionId }) => {
            const [title, subtitle, Icon] = FOOTER_ACTIONS[actionId];
            return <Link key={actionId} to={`/social/gallery/v2/share/${encodeURIComponent(context.token)}#sg-patron-action-${actionId}`}><Icon aria-hidden="true" /><span><strong>{title}</strong><small>{subtitle}</small></span></Link>;
          })}</nav>}
        </>}
        <footer className="eg-powered"><span>Powered by</span><img src={defaultLogo} alt="Haylo" /></footer>
      </div>
    </main>
  </>;
}
